// Subscription Tiers Configuration for Pitchallo
//
// NOTE: Pitchallo moved to a single flat plan ($60/mo or $600/yr AUD) —
// no more Basic/Standard/Premium. BASIC/STANDARD/PREMIUM keys are kept
// below, all pointing at the same plan definition, purely so any code
// still storing/reading clubData.subscriptionTier as 'basic', 'standard',
// or 'premium' keeps working without a data migration. New signups should
// be assigned a single canonical tier id — see PITCHALLO_TIER_ID below —
// but old values won't break.
//
// TODO: once every caller has been confirmed and cleaned up (see the
// upgrade/comparison helpers further down, which no longer make sense
// with one tier), simplify this file down to a single exported plan
// object instead of three identical keys.

const PLAN = {
  id: 'pitchallo',
  name: 'Pitchallo',
  price: 60,
  currency: 'AUD',
  interval: 'month',
  stripePriceId: 'price_1U77yJRQeD0eMHGLaFeCBpB5', // monthly price ID
  features: {
    pitches: 'unlimited',
    teams: 'unlimited',
    users: 'unlimited',
    admins: 'unlimited',
    totalUsers: 'unlimited',
    recurringAllocations: true,
    weatherAlerts: true,
    reports: 'advanced-printable',
    facilitySharing: true,
    satelliteMapping: true,
    support: 'email',
    backupManual: true,
    backupCloud: true,
    backupCloudFrequency: 90,
    changeLog: true,
    smartFixtureTool: true,
    shareLink: true,
    shareLinkSponsors: true,
    importAllocations: true
  },
  limits: {
    allocationsPerMonth: 'unlimited',
    allocationsFutureMonths: 'unlimited',
    storageGB: 50
  },
  description: 'Everything your club needs to manage pitches, teams and fixtures'
};

// Canonical tier id to assign on new signups going forward.
export const PITCHALLO_TIER_ID = 'pitchallo';

// All three legacy keys point at the same plan so existing lookups by
// 'basic' / 'standard' / 'premium' keep resolving instead of breaking.
export const SUBSCRIPTION_TIERS = {
  BASIC: PLAN,
  STANDARD: PLAN,
  PREMIUM: PLAN,
  PITCHALLO: PLAN
};

// Helper to get readable feature descriptions
export const FEATURE_DESCRIPTIONS = {
  pitches: 'Number of pitches you can manage',
  teams: 'Maximum teams in your system',
  users: 'User accounts with login access (excluding admins)',
  admins: 'Administrator accounts',
  totalUsers: 'Total user accounts (admins + regular users)',
  recurringAllocations: 'Automatically repeat weekly bookings',
  weatherAlerts: 'Real-time weather notifications',
  reports: 'Access to analytics and reports',
  facilitySharing: 'Share facilities with other clubs',
  satelliteMapping: 'AI-powered pitch detection',
  support: 'Support response time',
  backupManual: 'Export/import your data',
  backupCloud: 'Automated cloud backups',
  changeLog: 'Track all changes made to allocations',
  smartFixtureTool: 'AI-powered fixture optimization',
  shareLink: 'Public shareable schedule links',
  shareLinkSponsors: 'Add sponsor branding to shared links',
  importAllocations: 'Bulk import allocations from CSV/JSON files'
};

// Feature categories for display (e.g. on a "what's included" page)
export const FEATURE_CATEGORIES = {
  core: {
    name: 'Core Features',
    features: ['pitches', 'teams', 'users', 'admins', 'satelliteMapping']
  },
  scheduling: {
    name: 'Scheduling Tools',
    features: ['recurringAllocations', 'smartFixtureTool', 'shareLink', 'shareLinkSponsors', 'importAllocations']
  },
  management: {
    name: 'Management & Analytics',
    features: ['reports', 'changeLog', 'weatherAlerts', 'facilitySharing']
  },
  support: {
    name: 'Support & Backup',
    features: ['support', 'backupManual', 'backupCloud']
  }
};

// Get all tiers as an array (now returns one plan, deduplicated)
export const getAllTiers = () => {
  return [PLAN];
};

// Get a specific tier by ID — any legacy id resolves to the single plan
export const getTierById = (tierId) => {
  const tierKey = tierId?.toUpperCase();
  return SUBSCRIPTION_TIERS[tierKey] || PLAN;
};

// Helper functions for resource limit checking
export const getLimitsForTier = (tier) => {
  const tierData = getTierById(tier);

  return {
    displayName: tierData.name,
    maxPitches: tierData.features.pitches,
    maxTeams: tierData.features.teams,
    maxAdmins: tierData.features.admins,
    maxUsers: tierData.features.users,
    totalUsersAllowed: tierData.features.totalUsers,
    monthlyPrice: tierData.price
  };
};

// NOTE: these now explicitly handle 'unlimited' — the previous version did
// raw number math (limit - currentCount) which produced NaN once any
// limit became the string 'unlimited' instead of a number.
export const isLimitReached = (tier, resourceType, currentCount) => {
  const limits = getLimitsForTier(tier);

  const limitFor = (key) => limits[key];
  const reached = (limitKey) => {
    const limit = limitFor(limitKey);
    if (limit === 'unlimited') return false;
    if (typeof limit !== 'number') return false;
    return currentCount >= limit;
  };

  switch (resourceType) {
    case 'pitches':
      return reached('maxPitches');
    case 'teams':
      return reached('maxTeams');
    case 'users':
      return reached('maxUsers');
    case 'admins':
      return reached('maxAdmins');
    case 'totalUsers':
      return reached('totalUsersAllowed');
    default:
      return false;
  }
};

export const getRemainingCount = (tier, resourceType, currentCount) => {
  const limits = getLimitsForTier(tier);

  const remaining = (limitKey) => {
    const limit = limits[limitKey];
    if (limit === 'unlimited') return 'unlimited';
    if (typeof limit !== 'number') return 0;
    return Math.max(0, limit - currentCount);
  };

  switch (resourceType) {
    case 'pitches':
      return remaining('maxPitches');
    case 'teams':
      return remaining('maxTeams');
    case 'users':
      return remaining('maxUsers');
    case 'admins':
      return remaining('maxAdmins');
    case 'totalUsers':
      return remaining('totalUsersAllowed');
    default:
      return 0;
  }
};

// No higher tier exists anymore, so there is nothing to upgrade to.
// Kept as a function (rather than deleted) so any caller expecting an
// array doesn't crash — it just always gets an empty list now.
export const getUpgradeTiers = () => {
  return [];
};

// These *_LimitBenefits helpers previously described what you'd gain by
// upgrading. With one plan there's no upgrade path, so callers showing
// "upgrade and get X" prompts need to stop calling these — find any
// UI that renders getPitchLimitBenefits / getTeamLimitBenefits /
// getUserLimitBenefits and remove the upgrade prompt entirely, since
// showing it now would be advertising an upgrade that doesn't exist.
export const getPitchLimitBenefits = () => [];
export const getTeamLimitBenefits = () => [];
export const getUserLimitBenefits = () => [];

// No tier comparison is meaningful with a single plan — always empty.
export const compareTiers = () => {
  return {};
};

export default SUBSCRIPTION_TIERS;
